import { Building2, Play } from 'lucide-react';
import type { Product } from './types';
import { normalizeProductPricing } from './pricingNormalization';

export const facilitiesManagementSuite: Product = normalizeProductPricing({
  id: 'facilities-management-suite',
  title: 'Complete Facilities Management Suite (FMS)',
  subtitle: 'AI-Powered Facility Operations & Digital Twin Platform',
  description: 'Revolutionary facilities management platform combining AI intelligence, IoT integration, digital twin technology, and comprehensive compliance automation for UK enterprise operations with full HSE compliance.',
  icon: Building2,
  image: 'https://images.unsplash.com/photo-1487958449943-2429e8be8625?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  category: 'Facilities Management',
  status: 'Live',
  businessTypes: ['#enterprise', '#manufacturing', '#operations', '#real-estate', '#compliance', '#facilities'],
  features: [
    // Top 10 most business-relevant features
    'Generative AI Assistant for Natural Language Facility Requests',
    'Predictive Maintenance AI with Machine Learning Equipment Health',
    'Interactive 3D Digital Twin Facility Visualisation',
    'AI-Driven Space Optimisation & Occupancy Analytics',
    'UK Regulatory Compliance Automation (GDPR, HSE, Building Regulations)',
    'Centralized Multi-Site Facility Management',
    'Real-Time IoT Data Overlay & Live Monitoring',
    'Automated Work Order Generation & Intelligent Routing',
    'AI-Powered Energy Consumption Optimization',
    'Carbon Footprint Tracking & Reporting',

    // Additional features (11-42)
    'Self-Learning Asset Performance & Risk Scoring',
    'Augmented Reality (AR) Maintenance Guidance',
    'Virtual "What-If" Scenario Modeling & Simulation',
    'Immersive Facility Walkthrough & Space Planning',
    'Digital Twin-Based Emergency Response Planning',

    // Comprehensive Asset Management
    'Complete Asset Lifecycle Management & Tracking',
    'RFID & QR Code Integration with Mobile Scanning',
    'Automated Maintenance Scheduling & Parts Management',
    'Equipment History & Performance Analytics',
    'Asset Depreciation & Financial Reporting',
    'Warranty & Contract Management Integration',

    // Smart Building & IoT Integration
    'IoT Sensor Network & Real-Time Environmental Monitoring',
    'Smart HVAC & Energy Management Systems',
    'Indoor Air Quality (IAQ) & Wellness Monitoring',
    'Occupancy Sensors & Space Utilization Tracking',
    'Smart Lighting & Automated Building Controls',
    'Edge Computing for Real-Time Decision Making',

    // Compliance & Safety Management
    'Automated Safety Inspection & Audit Management',
    'Incident Reporting & Investigation Workflows',
    'Emergency Response & Crisis Management',
    'Fire Safety & Evacuation Planning',
    'Blockchain-Based Immutable Audit Trails',

    // Multi-Site Operations & Vendor Management
    'Vendor Performance Monitoring & Scorecards',
    'Smart Contract Automation for Service Payments',
    'SLA Tracking & Performance Analytics',
    'Cost Center Allocation & Budget Management',
    'Integrated Procurement & Invoice Processing',

    // Sustainability & Environmental
    'Energy Efficiency Monitoring & Optimization',
    'Waste Management & Recycling Programs',
    'Sustainability KPI Dashboard & Goal Tracking',
    'Green Building Certification Support',
    'Environmental Impact Assessment Tools',

    // Advanced Analytics & Reporting
    'Real-Time Operations Dashboard & KPI Monitoring',
    'Predictive Analytics & Trend Forecasting',
    'Cost Analysis & ROI Calculation Tools',
    'Custom Report Builder & Executive Dashboards',
    'Mobile App for Field Teams & Technicians',
    'API Integration with ERP, HR & Finance Systems',
  ],
  pricing: { setup: 35050, monthly: 1199 },
  color: 'from-emerald-500 to-teal-600',
  demoConfig: {
    type: 'interactive',
    url: '/facilities-management/demo',
    title: 'Experience Enterprise Facilities Intelligence',
    description: 'Explore our AI-powered facilities management suite with real facility data, predictive maintenance, digital twin technology, and intelligent resource allocation',
    buttonText: 'Launch Interactive Demo',
    icon: Play,
    buttonColor: 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700',
  },
});

